import { isNativePlatform } from "@/lib/capacitor/platform";
import type { WebVersionFeature } from "@/components/mobile/WebVersionModal";

/**
 * The single gate every purchase path calls before opening a checkout.
 *
 * WHY: on iOS AND Android, opening Stripe/Whop for a subscription, dial-credit
 * top-up, area code, fax unlock or A2P fee is Apple Guideline 3.1.1 / Play's
 * payments policy. Consumable credits and an embedded third-party checkout are
 * the two least defensible cases there are.
 *
 * ⚠️ WORDING IS A COMPLIANCE SURFACE TOO. Apple 3.1.3 (anti-steering) forbids
 * pointing users at an external place to buy. So this shows the existing
 * WebVersionModal, whose copy says a feature "is available on the web version"
 * and never mentions purchasing, pricing, or a URL to go buy at. Do not
 * "helpfully" reword it to say where to pay — that trades one rejection for
 * another.
 *
 * Returns TRUE only on web, where the checkout may proceed. On native it opens
 * the explainer modal and returns FALSE.
 */
const EVENT = "insura:native-purchase-blocked";

export function allowCheckout(feature: WebVersionFeature = "payment"): boolean {
  if (!isNativePlatform()) return true;
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { feature } }));
  } catch {
    /* modal host not mounted — blocking is still the correct outcome */
  }
  return false;
}

/** Used by the host component; kept here so the event name has one owner. */
export const NATIVE_PURCHASE_BLOCKED_EVENT = EVENT;
